/**
 * ch05's translation between the pinned client and what a planner is allowed to conclude.
 *
 * Everything here is pure and free of React, so the claims below are asserted in
 * `api.test.ts` without a render. Each function is one of four things, and each is
 * labelled the way ch00's own mapping module labels its own:
 *
 *   TRANSFORM  a shape change that loses nothing
 *   DERIVE     a value computed from what the contract does carry, stated as such
 *   GAP        something a screen wants that the contract does not publish
 *   REFUSAL    an arithmetic this module deliberately declines to do
 *
 * The two REFUSALs are the reason this file exists rather than the mapping living in the
 * screens:
 *
 *   * **A D4 row that is not evaluable has no delta, and no delta is not a delta of
 *     nought.** `plan.exit_shadow_comparison` enforces it in the database —
 *     `check ((is_evaluable is true) or (realised_price_delta is null))` — and the
 *     platform's harness reports *not evaluable* while its preconditions are open. A
 *     screen that rendered the absent delta as `0`, as an empty bar or as a dash with no
 *     explanation would be publishing "the shadow cadence recovered no margin", which is
 *     a false negative on the programme's central margin hypothesis. So `d4Verdict`
 *     returns a THIRD state and `d4Delta` returns `null` with the row's own reason codes
 *     attached.
 *   * **The override rate is not divided here.** `OverrideRate` publishes a count per
 *     `(disposition, reason_code)` pair precisely so the caller does not choose a
 *     denominator: the spec's own words are that "the arithmetic stays where it belongs —
 *     `planning.programme.override_rate` — rather than being done in SQL or on a screen
 *     (G11)". `overrideCounts` therefore sums within a reason code and never across the
 *     divide, and the screen renders the RATE as an absent measure with that reason.
 *
 * GAP, recorded once: `disposition` and `reason_code` are unconstrained strings in the
 * spec — there is no published vocabulary for either. So the reason-code picker is built
 * from the reason codes the queue itself carries (`reasonCodeGroups`) rather than
 * from a list invented here, and a code this build has no label for is rendered as the
 * datum it is instead of being dropped or given a plausible name.
 */

import type { TFunction } from 'i18next';

import type { Datum } from '@/charts';

import type {
  CadenceProposalSummary, ConsolidationDoorSummary, ConsolidationLineSummary,
  ConsolidationOptionSummary, D4EvidenceRow, ExitPlanSummary, HandoverReadinessSummary, OverrideRate,
  ReturnDispositionSummary, RouteRealisation, RoutingSummary, ShadowSeasonCount,
  TerminalPositionSummary, UnsignedPlanSummary,
} from '@/api/generated';

// ── decimals ─────────────────────────────────────────────────────────────────

/**
 * TRANSFORM — a decimal string into a number, or `null`.
 *
 * The platform sends `decimal(20, 6)` as a string so that JSON does not round it. An
 * unparseable or absent value becomes `null` and NEVER `0`: every caller of this function
 * feeds a `NumericCell`, and `0` is the one value that would be read as a measurement.
 */
export function parseDecimal(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value.trim() === '') return null;
  // Since bata-platform DEC-111 every exit rate is a decimal string at eight places (`rate_text`,
  // e.g. "83.33333333"), read by `Number` below and shown at each screen's own RATE_DIGITS /
  // RATIO_DIGITS (four places). Kept for a platform before it: `ExitPlanSummary.required_depletion_rate`
  // and `DepletionRateResponse.required_rate` were
  // `str(Fraction)` at bata-platform v0.61.0 (wiring.py `_required_rate`, routers/exit.py
  // `compute_required_rate`): an exact rational such as "250/3" whenever the weeks do not divide
  // the units. Read as the number it is, never as an absence (audit 2026-10-02 #5).
  const rational = /^\s*(-?\d+)\/(\d+)\s*$/.exec(value);
  if (rational !== null) {
    const denominator = Number(rational[2]);
    return denominator === 0 ? null : Number(rational[1]) / denominator;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// ── gate D4 — the honesty case ───────────────────────────────────────────────

/**
 * The three states a D4 row can be in, and they are three and not two.
 *
 * `improved` and `notImproved` are both *measurements*. `notEvaluable` is the absence of
 * one, and it is a first-class state rather than the empty case of the other two.
 */
export type D4Verdict = 'improved' | 'not-improved' | 'not-evaluable';

/** DERIVE — the verdict, with the not-evaluable state reachable before any arithmetic. */
export function d4Verdict(row: D4EvidenceRow): D4Verdict {
  if (!row.is_evaluable) return 'not-evaluable';
  const delta = parseDecimal(row.realised_price_delta);
  // Evaluable with no delta is still not a zero. The database permits `is_evaluable` true
  // with a null delta; the row is then evaluated but unmeasured, and it says so.
  if (delta === null) return 'not-evaluable';
  return delta > 0 ? 'improved' : 'not-improved';
}

/**
 * REFUSAL — the realised-price delta, or `null`.
 *
 * There is no `?? 0` in this function and there must never be one. `docs/05` §12 measures
 * D4 on the pair (realised price, terminal stock) and quotes this number; a zero would be
 * quoted as "no improvement" when the truth is "no evidence".
 */
export function d4Delta(row: D4EvidenceRow): number | null {
  return row.is_evaluable ? parseDecimal(row.realised_price_delta) : null;
}

/**
 * TRANSFORM — the row's own reason codes, in the order the platform stored them.
 *
 * Rendered as the reference data they are. There is no published vocabulary for these
 * codes, so translating them would mean inventing one; the screen states in its own copy
 * what "not evaluable" means and shows the platform's codes beside it.
 */
export function d4ReasonCodes(row: D4EvidenceRow): readonly string[] {
  return row.not_evaluable_reason_codes.filter((code) => code.trim() !== '');
}

export interface D4Standing {
  readonly rows: number;
  readonly evaluable: number;
  readonly notEvaluable: number;
  readonly improved: number;
  readonly notImproved: number;
  /**
   * Whether the gate can be read at all. False while every row is inadmissible, which is
   * the state the platform's harness reports today and the state the screen must render
   * as its headline rather than as an empty chart.
   */
  readonly measurable: boolean;
  /** Every distinct reason the rows give for being inadmissible, deduplicated. */
  readonly reasonCodes: readonly string[];
}

/** DERIVE — the standing of the gate over a page of evidence. Counts only; no ratio. */
export function d4Standing(rows: readonly D4EvidenceRow[]): D4Standing {
  const verdicts = rows.map(d4Verdict);
  const reasons = new Set<string>();
  for (const row of rows) for (const code of d4ReasonCodes(row)) reasons.add(code);
  const evaluable = verdicts.filter((v) => v !== 'not-evaluable').length;
  return {
    rows: rows.length,
    evaluable,
    notEvaluable: verdicts.filter((v) => v === 'not-evaluable').length,
    improved: verdicts.filter((v) => v === 'improved').length,
    notImproved: verdicts.filter((v) => v === 'not-improved').length,
    measurable: evaluable > 0,
    reasonCodes: [...reasons].sort(),
  };
}

// ── 05.P2 — the shadow-season counter ────────────────────────────────────────

/**
 * reqs/05 §4 Phase 2 requires a *complete* shadow season before the cadence engine may
 * drive a live price. One is the threshold and there is no partial credit: half a season
 * shadowed is not half a permission, which is why nothing in this module returns a
 * fraction of this number.
 */
export const SHADOW_SEASONS_REQUIRED = 1;

export interface ShadowStanding {
  readonly countryKey: string;
  readonly seasonsShadowed: number;
  /** The gate, as a boolean. Not a percentage and not a progress value. */
  readonly mayDriveLivePrices: boolean;
}

/** DERIVE — the `05.P2` verdict per country. */
export function shadowStanding(rows: readonly ShadowSeasonCount[]): readonly ShadowStanding[] {
  return rows.map((row) => ({
    countryKey: row.country_key,
    seasonsShadowed: row.seasons_shadowed,
    mayDriveLivePrices: row.seasons_shadowed >= SHADOW_SEASONS_REQUIRED,
  }));
}

// ── docs/05 §12 — dispositions per reason code ───────────────────────────────

export interface DispositionCount {
  readonly disposition: string;
  readonly count: number;
}

export interface ReasonCodeStanding {
  readonly reasonCode: string;
  readonly dispositions: readonly DispositionCount[];
  /** The sum within one reason code. A sum, never a quotient — see the file header. */
  readonly total: number;
}

/**
 * TRANSFORM — the flat `(disposition, reason_code, count)` rows into one entry per reason
 * code, dispositions sorted by name so two renders of the same data agree.
 *
 * REFUSAL: no rate is computed. The pair counts are published so that the reader chooses
 * the denominator, and a screen that picked one would be answering a question nobody
 * asked with a number that looks like the one they did.
 */
export function overrideCounts(rows: readonly OverrideRate[]): readonly ReasonCodeStanding[] {
  const byReason = new Map<string, Map<string, number>>();
  for (const row of rows) {
    const dispositions = byReason.get(row.reason_code) ?? new Map<string, number>();
    dispositions.set(row.disposition, (dispositions.get(row.disposition) ?? 0) + row.disposition_count);
    byReason.set(row.reason_code, dispositions);
  }
  return [...byReason.entries()]
    .map(([reasonCode, dispositions]) => {
      const entries = [...dispositions.entries()]
        .map(([disposition, count]) => ({ disposition, count }))
        .sort((a, b) => a.disposition.localeCompare(b.disposition));
      return {
        reasonCode,
        dispositions: entries,
        total: entries.reduce((sum, entry) => sum + entry.count, 0),
      };
    })
    .sort((a, b) => a.reasonCode.localeCompare(b.reasonCode));
}

// ── the reason-code vocabulary ───────────────────────────────────────────────

/**
 * Labels for the reason codes the exit engines actually send — keyed on bata-platform v0.61.0
 * `services/api/wiring.py` `EXIT_REASON_CODES`, the ceiling every exit row's `reason_code` is
 * checked against: twenty codes from `planning.exit.cadence`, `.shadow`, `.channel` and
 * `.clearance` (usability audit A7 — the map used to be keyed on reqs/05's prose names, which the
 * engine never sends, so 15 of the 20 had no words).
 *
 * The spec still types `reason_code` as an unconstrained string, so `reasonLabelKey` returns
 * `null` for a code outside this set and the caller renders the code itself — a code with a
 * wrong label is worse than a code with none, because the label is what a planner audits the
 * bulk action by. `exit-reason-words.test.ts` pins the map to the twenty, plus the withdrawal's
 * `zona_sales_withdrawal` (bata-platform ADR 7027), which writes lane-book lines outside that ceiling,
 * the outlet move's, and the units-sent cap's `outbound_units_cap_deferred` (`CAP_LANE_REASON_CODES`).
 */
export const REASON_LABEL_KEY: Readonly<Record<string, string>> = {
  // planning.exit.cadence — why a cadence proposal exists
  rate_gap: 'exit.reason.rateGap',
  handover_unreachable: 'exit.reason.handoverUnreachable',
  at_floor: 'exit.reason.atFloor',
  hold_cheaper: 'exit.reason.holdCheaper',
  on_plan: 'exit.reason.onPlan',
  // planning.exit.shadow — why a week produced no proposal, or a live run was refused
  shadow_season_incomplete: 'exit.reason.shadowSeasonIncomplete',
  protected_from_markdown: 'exit.reason.protectedFromMarkdown',
  elasticity_unavailable: 'exit.reason.elasticityUnavailable',
  velocity_uncorrected: 'exit.reason.velocityUncorrected',
  ladder_refused: 'exit.reason.ladderRefused',
  elasticity_coverage_below_target: 'exit.reason.elasticityCoverageBelowTarget',
  promotion_baseline_unguarded: 'exit.reason.promotionBaselineUnguarded',
  // planning.exit.channel — why a route won or was excluded
  highest_realisation: 'exit.reason.highestRealisation',
  brand_gated: 'exit.reason.brandGated',
  capacity_exhausted: 'exit.reason.capacityExhausted',
  below_scrap_value: 'exit.reason.belowScrapValue',
  // planning.exit.clearance — why a movement was proposed, or was not. The last two are the pair
  // the module's own comment insists on keeping apart: "this lane does not pay" and "we cannot
  // price this lane" are different findings with different remedies.
  size_run_repair: 'exit.reason.sizeRunRepair',
  door_consolidated: 'exit.reason.doorConsolidated',
  transfer_uneconomic: 'exit.reason.laneNotWorthIt',
  lane_not_costed: 'exit.reason.awaitingLaneCost',
  // planning.exit.withdrawal (bata-platform ADR 7027, bata-reqs issue 35 P2b) — the forced move
  // out of a door with no zona-sales area once an article is accepted into ZONA_SALES. Not a
  // clearance-engine code: no economic test was passed or failed, the planner's decision ordered it.
  zona_sales_withdrawal: 'exit.reason.zonaSalesWithdrawal',
  // planning.exit.outlet_exit (bata-platform ADR 7028, issue 35 P4) — the door-to-door move to an
  // outlet door once an article is accepted into OUTLET. Forced like the withdrawal's.
  outlet_exit_transfer: 'exit.reason.outletExitTransfer',
  // planning.allocation.movecap `DEFERRAL_OUTBOUND_UNITS_CAP` (bata-reqs ADR 9000, platform #888/#891):
  // a line the sending store's weekly cap on pairs sent held back, written withheld with its units —
  // a consolidation line, or a forced withdrawal or outlet move under a cap of 0.
  outbound_units_cap_deferred: 'exit.reason.outboundUnitsCapDeferred',
};

export function reasonLabelKey(code: string): string | null {
  return Object.prototype.hasOwnProperty.call(REASON_LABEL_KEY, code) ? REASON_LABEL_KEY[code] ?? null : null;
}

/** The reason the units-sent cap writes (bata-reqs ADR 9000). It binds at the line's SENDING store. */
export const OUTBOUND_UNITS_CAP_DEFERRED = 'outbound_units_cap_deferred';

/**
 * A lane line's reason in words, naming the store whose cap bound it where the reason is the
 * units-sent cap's: the cap is charged to the origin (platform `movecap.py`, `bound_at` = the
 * origin), so `sender` — the line's source store as the screen names it — is that store. Any other
 * code reads as `reasonLabelKey` says, or as the code itself.
 */
export function laneReasonWords(
  code: string, translate: TFunction, sender: string | null = null,
): string {
  if (code === OUTBOUND_UNITS_CAP_DEFERRED && sender !== null && sender !== '') {
    return translate('exit.reason.outboundUnitsCapDeferredAt', { store: sender });
  }
  const key = reasonLabelKey(code);
  return key === null ? code : translate(key);
}

// ── X3 — clearance consolidation ─────────────────────────────────────────────

/**
 * Labels for the node roles `planning.exit.clearance` assigns, and only for those.
 *
 * GAP, the same one as `reason_code`: the spec types `node_role` as an unconstrained
 * string. Three values exist in the engine — `source`, `destination` and `neither` — and
 * the third is not a tidy-up: its own docstring says a POSITION-only record carries it,
 * meaning the door's residual is measured and no run has yet decided anything about it.
 * That is not the same statement as "a run looked at this door and left it alone", which
 * is a `destination`. A role this build does not know is rendered as the datum it is.
 */
const NODE_ROLE_LABEL_KEY: Readonly<Record<string, string>> = {
  source: 'exit.nodeRole.source',
  destination: 'exit.nodeRole.destination',
  neither: 'exit.nodeRole.neither',
};

export function nodeRoleLabelKey(role: string): string | null {
  return NODE_ROLE_LABEL_KEY[role] ?? null;
}

export interface DoorStanding {
  readonly doors: number;
  readonly brokenRuns: number;
  readonly residualUnits: number;
  /**
   * Doors with no `sell_through_per_capacity`. The spec's own note: "a door with no
   * capacity on record has no ranking, and a zero would sort it to the bottom as though
   * it had been measured and found wanting." Counted so the screen can say how much of
   * the ranking is missing rather than presenting a partial order as a complete one.
   */
  readonly unranked: number;
}

/** DERIVE — the door position over a page of doors. Counts and a sum; no ratio. */
export function doorStanding(rows: readonly ConsolidationDoorSummary[]): DoorStanding {
  return {
    doors: rows.length,
    brokenRuns: rows.filter((row) => row.run_is_broken).length,
    residualUnits: rows.reduce((sum, row) => sum + row.residual_units, 0),
    unranked: rows.filter((row) => parseDecimal(row.sell_through_per_capacity) === null).length,
  };
}

/**
 * How many rows of the option directory the consolidation screen draws.
 *
 * A presentation bound, not a data bound: the directory holds every option at the date and
 * its totals are over all of them. Drawing a few thousand interactive rows would make the
 * table unusable without making it more complete, so the head of the ordered list is drawn,
 * the filter reaches any option, and the panel states how many it is not showing.
 */
export const OPTION_ROWS_DRAWN = 50;

/**
 * Rows per page when reading the option directory — a page size, not a cap.
 *
 * Measured on production on 2026-09-24: CO's 2026-09-06 position holds 7,515 options over
 * 353,323 option × door rows. At the platform's default page of 100 rows the directory is 76
 * pages, past `MAX_PAGES` (50), and `collectPages` would refuse it as an unbounded
 * perimeter. At 500 it is 16 pages, with room for the estate to triple. The platform's
 * ceiling (`BATA_MAXIMUM_PAGE_SIZE`, default 1,000) admits it. Every row is still read; only
 * the number of round trips changes.
 */
export const OPTION_PAGE_ROWS = 500;

export interface OptionDirectory {
  /** Every option in the roll-up, and its doors and residual summed — never the drawn subset. */
  readonly options: number;
  readonly doors: number;
  readonly residualUnits: number;
  /** Options whose key contains the filter text. Equal to `options` with no filter. */
  readonly matching: number;
  /** Every matching option, largest residual first — what the CSV carries (`docs/adr/9008`). */
  readonly ordered: readonly ConsolidationOptionSummary[];
  /** The matching options, largest residual first, cut to `OPTION_ROWS_DRAWN`. */
  readonly drawn: readonly ConsolidationOptionSummary[];
}

/**
 * DERIVE — the option directory the consolidation screen chooses from.
 *
 * Totals are counts and sums of the platform's own roll-up; nothing is re-derived from doors.
 * Ordered largest residual first because that is where consolidation has the most to move,
 * with the option key as a tie-break so the order is total and a re-render never reshuffles.
 */
export function optionDirectory(
  rows: readonly ConsolidationOptionSummary[],
  filter: string,
): OptionDirectory {
  const needle = filter.trim().toLowerCase();
  const matching = needle === ''
    ? [...rows]
    : rows.filter((row) => row.option_id.toLowerCase().includes(needle));
  matching.sort((left, right) => (
    right.residual_units - left.residual_units || left.option_id.localeCompare(right.option_id)
  ));
  return {
    options: rows.length,
    doors: rows.reduce((sum, row) => sum + row.doors, 0),
    residualUnits: rows.reduce((sum, row) => sum + row.residual_units, 0),
    matching: matching.length,
    ordered: matching,
    drawn: matching.slice(0, OPTION_ROWS_DRAWN),
  };
}

export interface LaneStanding {
  readonly lanes: number;
  readonly proposed: number;
  readonly withheld: number;
  /** Withheld because reqs/05 D8's lane cost has not landed. A subset of `withheld`. */
  readonly uncosted: number;
  /** Units on PROPOSED lanes only — see the refusal below. */
  readonly proposedUnits: number;
  readonly runsRepaired: number;
  /** Every distinct reason the withheld lanes give, deduplicated and sorted. */
  readonly withheldReasonCodes: readonly string[];
}

/**
 * DERIVE — the lane position over a page of candidate lines.
 *
 * REFUSAL: `proposedUnits` sums the units on proposed lanes and never on all of them.
 * A withheld lane's units are units the plan does NOT move, and adding them to a headline
 * called "units consolidation would move" would report a transfer volume the engine
 * declined to recommend — in a chapter whose §7 names over-consolidation as a risk in its
 * own right. `runsRepaired` is counted on the same footing: a repair a withheld lane would
 * have made is not a repair.
 */
export function laneStanding(rows: readonly ConsolidationLineSummary[]): LaneStanding {
  const proposed = rows.filter((row) => row.is_proposed);
  const withheld = rows.filter((row) => !row.is_proposed);
  const reasons = new Set<string>();
  for (const row of withheld) if (row.reason_code.trim() !== '') reasons.add(row.reason_code);
  return {
    lanes: rows.length,
    proposed: proposed.length,
    withheld: withheld.length,
    uncosted: rows.filter((row) => parseDecimal(row.lane_cost_amount) === null).length,
    proposedUnits: proposed.reduce((sum, row) => sum + row.units, 0),
    runsRepaired: proposed.filter((row) => row.repairs_size_run).length,
    withheldReasonCodes: [...reasons].sort(),
  };
}

/**
 * What consolidating this page would actually get you — the potential the screen has never
 * stated, from fields it has always read.
 *
 * The lane table has carried `lane_cost_amount`, `incremental_revenue_amount` and
 * `net_benefit_amount` per line since it existed, and the headline above it carries units
 * and runs and no money at all. So the one question a country manager asks before agreeing
 * to move freight — *what is this worth, and how many doors does it close* — was answerable
 * only by reading a table row by row and adding up in their head.
 *
 * THREE REFUSALS, EACH OF WHICH WOULD OTHERWISE PRODUCE A CONFIDENT WRONG NUMBER.
 *
 * **1 — Money in two currencies does not add.** `ConsolidationLineSummary` carries its own
 * `currency_code`, and a page may span more than one. Summing across them produces a figure
 * in no currency, which reads as a total and is not one. Where the proposed lanes span more
 * than one currency this returns `null` for every amount and names the currencies, because
 * a refusal a planner can see beats a number nobody can attribute.
 *
 * **2 — A null amount is not nought.** reqs/05 D8's lane cost has not landed for every
 * lane, and the contract says so with `null`. Treating those as zero would report the
 * cheapest possible plan as though it had been priced — over-consolidation priced at zero
 * is the failure reqs/05 §7 names in its own right. Only costed lanes are summed, and the
 * count of the others travels with the sum so the total is read as the partial one it is.
 *
 * **3 — A door is emptied only if EVERYTHING leaves it.** The units on proposed lanes
 * leaving a door are compared against that door's published `residual_units` for the same
 * option. A door that sheds most of its residual is not a door closed, and counting it as
 * one would overstate the single figure this whole panel exists to state.
 *
 * WHAT IT IS NOT. This is not `compaction_score`. That measure is published — reqs/05 §3.8,
 * complete-run-equivalent units per door, and HIGHER is more compact — and
 * `POST /exit/position/compaction` computes it, but the request requires `scale_size`, the
 * length of the option's size scale, and no read in the contract publishes it. Counting the
 * sizes on `/preseason/size-availability` would count sizes WITH AVAILABILITY rather than
 * sizes in the scale, which is a different measure, across a chapter boundary the contract
 * does not join; and a constant here would be a countable thing bounded by code (G5). So
 * the index is not computed and not approximated. Note also that it runs the opposite way
 * from `bata-data`'s `exit_door_ceiling_ratio`, which is doors per run and lower-is-better:
 * ADR 0066 records that the two are different formulas, and mixing them inverts the sign.
 *
 * Over the PAGE, like `doorStanding` and `laneStanding` beside it. Both reads are cursor
 * paginated and the screen asks for one page of each.
 */
export interface ConsolidationPotential {
  /** Doors on this page that would hold none of their option's residual afterwards. */
  readonly doorsEmptied: number;
  /** Doors on this page holding residual at all — the denominator `doorsEmptied` is out of. */
  readonly doorsHolding: number;
  /** Units the proposed lanes move. Mirrors `laneStanding.proposedUnits` deliberately. */
  readonly unitsMoved: number;
  /** Summed over COSTED proposed lanes only, or `null` where the page spans currencies. */
  readonly netBenefit: number | null;
  readonly laneCost: number | null;
  readonly incrementalRevenue: number | null;
  /** Proposed lanes whose amounts are absent, and so are in none of the sums above. */
  readonly unpricedLanes: number;
  /** Proposed lanes that ARE in the sums. Stated so a partial total is read as partial. */
  readonly pricedLanes: number;
  /** Every currency the proposed lanes name, sorted. More than one means no sum. */
  readonly currencies: readonly string[];
}

/** DERIVE — the potential of the proposed lanes, over one page of lines and doors. */
export function consolidationPotential(
  lines: readonly ConsolidationLineSummary[],
  doors: readonly ConsolidationDoorSummary[],
): ConsolidationPotential {
  const proposed = lines.filter((row) => row.is_proposed);

  const currencies = [...new Set(proposed.map((row) => row.currency_code))].sort();
  // One currency or none. Zero proposed lanes is not a currency conflict — it is an empty
  // sum, which is legitimately nought units and legitimately nought money.
  const addable = currencies.length <= 1;

  // A lane counts as priced only if it publishes ALL THREE amounts. A lane with a revenue
  // and no cost contributes to one sum and not the others, and three totals drawn from
  // three different subsets of the same lanes do not reconcile with each other on screen.
  const priced = proposed.filter((row) => (
    parseDecimal(row.lane_cost_amount) !== null
    && parseDecimal(row.incremental_revenue_amount) !== null
    && parseDecimal(row.net_benefit_amount) !== null
  ));

  const total = (pick: (row: ConsolidationLineSummary) => string | null | undefined): number | null => (
    addable ? priced.reduce((sum, row) => sum + (parseDecimal(pick(row)) ?? 0), 0) : null
  );

  /*
    A door is keyed on (option, store) in both reads — `option_id` with `store_key` on a
    door, `option_id` with `source_store_key` on a line — so the join is the contract's own
    grain rather than one invented here.
  */
  // NESTED rather than a composite string key. Joining two keys with a separator needs a
  // character neither of them can contain, and an option key and a store key are estate
  // data whose alphabet this repository does not get to assume (G5). Nesting has no
  // separator to choose and no collision to reason about.
  const leaving = new Map<string, Map<string, number>>();
  for (const row of proposed) {
    const byDoor = leaving.get(row.option_id) ?? new Map<string, number>();
    byDoor.set(row.source_store_key, (byDoor.get(row.source_store_key) ?? 0) + row.units);
    leaving.set(row.option_id, byDoor);
  }

  const holding = doors.filter((door) => door.residual_units > 0);
  const doorsEmptied = holding.filter((door) => {
    const moved = leaving.get(door.option_id)?.get(door.store_key) ?? 0;
    // `>=` and not `===`: a page of lines may carry more units than the door page shows for
    // the same option, and a door cannot hold less than nothing. Under is not emptied.
    return moved >= door.residual_units;
  }).length;

  return {
    doorsEmptied,
    doorsHolding: holding.length,
    unitsMoved: proposed.reduce((sum, row) => sum + row.units, 0),
    netBenefit: total((row) => row.net_benefit_amount),
    laneCost: total((row) => row.lane_cost_amount),
    incrementalRevenue: total((row) => row.incremental_revenue_amount),
    unpricedLanes: proposed.length - priced.length,
    pricedLanes: priced.length,
    currencies,
  };
}

// ── the bulk unit ────────────────────────────────────────────────────────────

/** One reason-code group of the queue: the bulk unit docs/05 §7 defines. */
export interface ReasonCodeGroup {
  readonly reasonCode: string;
  readonly proposalIds: readonly string[];
  /** The countries the group spans. A bulk action is scoped to one — see `bulkScope`. */
  readonly countryKeys: readonly string[];
  readonly asOfDates: readonly string[];
}

interface QueueProposal {
  readonly proposal_id: string;
  readonly reason_code: string;
  readonly country_key: string;
  readonly as_of_date: string;
}

/**
 * TRANSFORM — the cadence queue into its reason-code groups.
 *
 * The picker's options come from here rather than from a constant, which is what makes
 * the vocabulary the queue's own instead of this repository's guess at it.
 */
export function reasonCodeGroups(
  proposals: readonly QueueProposal[],
): readonly ReasonCodeGroup[] {
  const groups = new Map<string, { ids: string[]; countries: Set<string>; dates: Set<string> }>();
  for (const proposal of proposals) {
    const group = groups.get(proposal.reason_code)
      ?? { ids: [], countries: new Set<string>(), dates: new Set<string>() };
    group.ids.push(proposal.proposal_id);
    group.countries.add(proposal.country_key);
    group.dates.add(proposal.as_of_date);
    groups.set(proposal.reason_code, group);
  }
  return [...groups.entries()]
    .map(([reasonCode, group]) => ({
      reasonCode,
      proposalIds: group.ids,
      countryKeys: [...group.countries].sort(),
      asOfDates: [...group.dates].sort(),
    }))
    .sort((a, b) => a.reasonCode.localeCompare(b.reasonCode));
}

export type BulkScope =
  | { readonly kind: 'ready'; readonly countryKey: string; readonly asOfDate: string }
  | { readonly kind: 'ambiguous' };

/**
 * DERIVE — the scope of a bulk action over one group.
 *
 * `BulkApproveRequest` carries exactly one `country_key` and one `as_of_date`, so a group
 * spanning two of either cannot be written as one action. Picking the first would file
 * thirty proposals under a country that did not raise them, and the audit trail reqs/07
 * §3.5 test *(d)* reads is the thing that would be wrong. The action is refused instead,
 * on the screen, with the reason named.
 */
export function bulkScope(group: ReasonCodeGroup): BulkScope {
  const countryKey = group.countryKeys[0];
  const asOfDate = group.asOfDates[0];
  if (group.countryKeys.length !== 1 || group.asOfDates.length !== 1
    || countryKey === undefined || asOfDate === undefined) {
    return { kind: 'ambiguous' };
  }
  return { kind: 'ready', countryKey, asOfDate };
}

// ── named refusals ───────────────────────────────────────────────────────────
//
// `refusalKey` lived here: a private map from `planning.exit.proposal_already_disposed`
// and `planning.exit.override_reason_required` to messages in this namespace, written
// because `KNOWN_PROBLEM_CODES` in `src/api/problem.ts` carried neither and that file was
// not ch05's to edit. Its own comment said it was reported for promotion rather than fixed
// in place. Both codes are now registered in `src/api/problem.ts`, in all three
// `locale/*/api.json` catalogues and in the dynamic-key registry, so every chapter's
// `ProblemNotice` names both refusals and this map is deleted — `docs/adr/0015`.

// ── X3's bulk unit — the consolidation lane book ──────────────────────────────

/**
 * One reason-code group of the lane book, and what a planner needs to know before
 * disposing of it.
 *
 * The group is the bulk unit for the same reason it is on the cadence queue: docs/05 §7
 * fixes the reason code as the unit an action is audited by, and reqs/07 §3.5's autonomy
 * register reads override rate *per reason code*. A perimeter assembled on the screen —
 * every row currently rendered, every row matching some client-side filter — could not be
 * reconstructed a month later, because the lane book is recomputed every run.
 *
 * The four counts beside the identifiers are not decoration. `ConsolidationLineSummary`
 * mixes lanes the engine PROPOSED with lanes it WITHHELD, and it does so deliberately —
 * the withheld ones are returned rather than filtered so a planner can see the refusals.
 * A disposition over a group is therefore a decision over a mixture, and the planner has
 * to be told the composition before they take it: approving a group that is nine tenths
 * `lane_not_costed` is approving movements nobody has priced, which is the
 * over-consolidation failure reqs/05 §7 names in its own right.
 */
export interface ConsolidationLineGroup {
  readonly reasonCode: string;
  readonly lineIds: readonly string[];
  /** Units across every line in the group — proposed and withheld alike. */
  readonly units: number;
  /** Lines the engine proposed. */
  readonly proposed: number;
  /** Lines the engine declined to propose. `proposed + withheld === lineIds.length`. */
  readonly withheld: number;
  /** Lines with no `lane_cost_amount` at all. A subset, and the loudest warning. */
  readonly uncosted: number;
  /** The countries the group spans, sorted. Reported, never used to narrow the write. */
  readonly countryKeys: readonly string[];
}

/**
 * TRANSFORM — the lane book into its reason-code groups.
 *
 * Sorted by code rather than by size, matching {@link reasonCodeGroups}: the order is a
 * property of the data and not of whichever page arrived first, and the two pickers in
 * this chapter should not order the same vocabulary two different ways.
 *
 * Note what this does NOT do. It does not drop withheld lines, it does not merge the two
 * refusal codes, and it does not compute a proposed-only unit total — `laneStanding`
 * already refuses that arithmetic for the headline and the refusal holds here. `units` is
 * every line in the group because the group is what the action writes: a count that
 * silently excluded the withheld lines would understate what the planner is disposing of.
 */
export function consolidationLineGroups(
  lines: readonly ConsolidationLineSummary[],
): readonly ConsolidationLineGroup[] {
  const groups = new Map<string, {
    ids: string[]; units: number; proposed: number; uncosted: number; countries: Set<string>;
  }>();
  for (const line of lines) {
    const group = groups.get(line.reason_code)
      ?? { ids: [], units: 0, proposed: 0, uncosted: 0, countries: new Set<string>() };
    group.ids.push(line.line_id);
    group.units += line.units;
    if (line.is_proposed) group.proposed += 1;
    if (parseDecimal(line.lane_cost_amount) === null) group.uncosted += 1;
    group.countries.add(line.country_key);
    groups.set(line.reason_code, group);
  }
  return [...groups.entries()]
    .map(([reasonCode, group]) => ({
      reasonCode,
      lineIds: group.ids,
      units: group.units,
      proposed: group.proposed,
      withheld: group.ids.length - group.proposed,
      uncosted: group.uncosted,
      countryKeys: [...group.countries].sort(),
    }))
    .sort((a, b) => a.reasonCode.localeCompare(b.reasonCode));
}

// ── the code vocabularies this build has labels for ──────────────────────────
//
// Every one of the maps below is a LABEL LOOKUP and not a vocabulary. reqs/05's
// models type each of these fields as an unconstrained string, and `13` §3 puts a
// controlled vocabulary in `data/vocabularies/` with an owner, a ratification date and a
// retirement rule — none of which this repository can supply. So the rule from
// `reasonLabelKey` holds throughout: a code this build recognises gets its catalogue
// label, and a code it does not is rendered as the datum it is. Nothing here invents a
// member, drops a row, or folds an unknown code into a known one.
//
// Where the code was read off the platform's own source it says so, because the
// difference between "reqs/05 names this route" and "the service emits this string"
// is the difference between a label that will resolve and one that will not.

/** Case-insensitive, because `13` §3 stores codes upper-case and the engines lower. */
function codeLabelKey(
  map: Readonly<Record<string, string>>, code: string | null | undefined,
): string | null {
  if (code === null || code === undefined) return null;
  const normalised = code.trim().toLowerCase();
  return normalised === '' ? null : map[normalised] ?? null;
}

/**
 * The four states `planning.exit.cadence.proposal_state` returns, verbatim.
 *
 * A different axis from `reason_code`, which says *why*. The service's own comment
 * makes the distinction and the screen keeps them in two columns for it.
 */
const CADENCE_STATE_LABEL: Readonly<Record<string, string>> = {
  proposed: 'exit.state.proposed',
  protected: 'exit.state.protected',
  floor_reached: 'exit.state.floorReached',
  awaiting_recovery: 'exit.state.awaitingRecovery',
};

export type StatusLevel = 'good' | 'warning' | 'serious' | 'critical';

const CADENCE_STATE_LEVEL: Readonly<Record<string, StatusLevel>> = {
  proposed: 'good',
  protected: 'good',
  floor_reached: 'serious',
  awaiting_recovery: 'warning',
};

export function cadenceStateLabelKey(state: string | null | undefined): string | null {
  return codeLabelKey(CADENCE_STATE_LABEL, state);
}

/**
 * A state this build does not recognise is a `warning`, never a `good`.
 *
 * The level drives an icon, and an icon is a claim. Reading an unknown state as healthy
 * is the one failure mode that cannot be seen on the screen — so the unknown case takes
 * the level that says *look at this*, and the label beside it is the raw code.
 */
export function cadenceStateLevel(state: string | null | undefined): StatusLevel {
  const normalised = (state ?? '').trim().toLowerCase();
  return CADENCE_STATE_LEVEL[normalised] ?? 'warning';
}

/** `planning.exit.handover` — `ready`, `at_risk`, `not_ready`, and three not two. */
const HANDOVER_STATE_LABEL: Readonly<Record<string, string>> = {
  ready: 'exit.state.ready',
  at_risk: 'exit.state.atRisk',
  not_ready: 'exit.state.notReady',
};

const HANDOVER_STATE_LEVEL: Readonly<Record<string, StatusLevel>> = {
  ready: 'good',
  at_risk: 'warning',
  not_ready: 'critical',
};

export function handoverStateLabelKey(state: string | null | undefined): string | null {
  return codeLabelKey(HANDOVER_STATE_LABEL, state);
}

export function handoverStateLevel(state: string | null | undefined): StatusLevel {
  const normalised = (state ?? '').trim().toLowerCase();
  return HANDOVER_STATE_LEVEL[normalised] ?? 'warning';
}

/** `planning.exit.cadence.cheaper_branch` — `step` or `hold`, or nothing at all. */
const COUNTERFACTUAL_LABEL: Readonly<Record<string, string>> = {
  step: 'exit.cadence.stepWins',
  hold: 'exit.cadence.holdWins',
};

export function counterfactualLabelKey(winner: string | null | undefined): string | null {
  return codeLabelKey(COUNTERFACTUAL_LABEL, winner);
}

/**
 * The lifecycle classes `13` M1 enumerates, plus the three reqs/05 §3.1 writes its TSR
 * targets against.
 *
 * Both sets are here and neither is preferred, because the two do not agree and this
 * repository is not the place that settles it: `data/vocabularies/lifecycle_class.yml`
 * publishes `CORE`, `SEASONAL`, `DROP`, `CAPSULE`, `UNKNOWN`, while reqs/05 §3.1 cascades
 * targets for *fashion*, *seasonal core* and *carry-over*. The exit plan's
 * `lifecycle_class` is an unconstrained string fed from a parameter register, so either
 * could arrive. A class in neither set renders as its code.
 */
const LIFECYCLE_LABEL: Readonly<Record<string, string>> = {
  core: 'exit.lifecycle.core',
  seasonal: 'exit.lifecycle.seasonal',
  drop: 'exit.lifecycle.drop',
  capsule: 'exit.lifecycle.capsule',
  unknown: 'exit.lifecycle.unknown',
  fashion: 'exit.lifecycle.fashion',
  carryover: 'exit.lifecycle.carryover',
};

export function lifecycleLabelKey(lifecycleClass: string): string | null {
  return codeLabelKey(LIFECYCLE_LABEL, lifecycleClass);
}

/** The eight exit routes reqs/05 §3.5 enumerates. There is no registry file for them. */
const ROUTE_LABEL: Readonly<Record<string, string>> = {
  own_store_clearance: 'exit.route.ownStoreClearance',
  outlet: 'exit.route.outlet',
  franchise_wholesale: 'exit.route.franchiseWholesale',
  jobber: 'exit.route.jobber',
  secondary_market: 'exit.route.secondaryMarket',
  pack_away: 'exit.route.packAway',
  carry_over: 'exit.route.carryOver',
  scrap: 'exit.route.scrap',
};

export function routeLabelKey(routeCode: string): string | null {
  return codeLabelKey(ROUTE_LABEL, routeCode);
}

/**
 * The ONE `unavailable_reason_code` the platform emits today.
 *
 * `services/api/routers/exit.py` publishes `gated_by_brand_protection` and nothing else;
 * `pub_exit_route_realisation` carries no reason column in `bata-data` at all. The three
 * other reasons this chapter used to name — no realisation history, no pack-away
 * capacity, no next-season forecast — were the worked example's own strings and no route
 * ever returns them, so they are gone rather than kept as labels for codes that cannot
 * arrive. When the store starts publishing its reasons, each one is a label here and a
 * line in the catalogue, not a guess.
 */
const ROUTE_UNAVAILABLE_LABEL: Readonly<Record<string, string>> = {
  gated_by_brand_protection: 'exit.unavailable.brandProtection',
};

export function routeUnavailableLabelKey(code: string | null | undefined): string | null {
  return codeLabelKey(ROUTE_UNAVAILABLE_LABEL, code);
}

/** reqs/05 D9 — which basis produced the rate, so a parameter is not read as measured. */
const REALISATION_BASIS_LABEL: Readonly<Record<string, string>> = {
  fitted: 'exit.basis.fitted',
  parameter: 'exit.basis.parameter',
};

export function realisationBasisLabelKey(basis: string | null | undefined): string | null {
  return codeLabelKey(REALISATION_BASIS_LABEL, basis);
}

/**
 * `planning.exit.returns` — `restock`, `route`, `refurbish`, `scrap`.
 *
 * Deliberately NOT the planner vocabulary published as `Disposition`. The service's own
 * comment says why: this is where a unit went, that is what a human decided about a
 * recommendation, and one enum over both would let a queue filtered on `approve` return
 * refurbished stock.
 */
const RETURN_DISPOSITION_LABEL: Readonly<Record<string, string>> = {
  restock: 'exit.disposition.restock',
  route: 'exit.disposition.route',
  refurbish: 'exit.disposition.refurbish',
  scrap: 'exit.disposition.scrap',
};

export function returnDispositionLabelKey(disposition: string): string | null {
  return codeLabelKey(RETURN_DISPOSITION_LABEL, disposition);
}

/**
 * The resulting condition grades. reqs/05 §8 criterion 9 makes the refurbished grade
 * distinct from grade A on purpose, and `counts_toward_full_price` is the flag that
 * carries the exclusion — the grade label is the explanation beside it, never the rule.
 */
const GRADE_LABEL: Readonly<Record<string, string>> = {
  a: 'exit.grade.a',
  b: 'exit.grade.b',
  c: 'exit.grade.c',
  refurbished: 'exit.grade.refurbished',
  scrapped: 'exit.grade.scrapped',
};

export function gradeLabelKey(gradeCode: string): string | null {
  return codeLabelKey(GRADE_LABEL, gradeCode);
}

/**
 * The three verdicts `POST /exit/consolidation/lane-economics` returns.
 *
 * `awaiting_lane_cost` and `lane_below_cost_hurdle` are two answers and not one, which is
 * the whole reason the endpoint refuses rather than costing at zero: an unlanded feed
 * must not read as a commercial judgement.
 */
const LANE_VERDICT_LABEL: Readonly<Record<string, string>> = {
  lane_is_economic: 'exit.lane.isEconomic',
  awaiting_lane_cost: 'exit.lane.awaitingCost',
  lane_below_cost_hurdle: 'exit.lane.belowHurdle',
};

export function laneVerdictLabelKey(code: string): string | null {
  return codeLabelKey(LANE_VERDICT_LABEL, code);
}

// ── X2 — the cadence queue, counted ──────────────────────────────────────────

export interface CadenceStanding {
  readonly proposals: number;
  readonly proposed: number;
  readonly protectedLines: number;
  readonly atFloor: number;
  readonly awaitingRecovery: number;
  /**
   * Rows whose `state` the service left null or this build does not recognise. Reported
   * separately and never added to `proposed`: a row of unknown standing is not a row
   * ready to act on, and a headline that swallowed it would overstate the queue.
   */
  readonly stateUnstated: number;
  readonly stepWins: number;
  readonly holdWins: number;
  /** Rows with no counterfactual at all — at the floor, or awaiting recovery. */
  readonly noCounterfactual: number;
  /** Protected rows carrying no carry-over NPV. reqs/05 §8 criterion 7 wants none. */
  readonly protectedWithoutNpv: number;
}

/** DERIVE — counts over a page of proposals. Counts only; nothing is divided. */
export function cadenceStanding(rows: readonly CadenceProposalSummary[]): CadenceStanding {
  const state = (row: CadenceProposalSummary) => (row.state ?? '').trim().toLowerCase();
  const isProtected = rows.filter((row) => state(row) === 'protected');
  const winners = rows.map((row) => (row.counterfactual_winner ?? '').trim().toLowerCase());
  return {
    proposals: rows.length,
    proposed: rows.filter((row) => state(row) === 'proposed').length,
    protectedLines: isProtected.length,
    atFloor: rows.filter((row) => state(row) === 'floor_reached').length,
    awaitingRecovery: rows.filter((row) => state(row) === 'awaiting_recovery').length,
    stateUnstated: rows.filter((row) => CADENCE_STATE_LABEL[state(row)] === undefined).length,
    stepWins: winners.filter((winner) => winner === 'step').length,
    holdWins: winners.filter((winner) => winner === 'hold').length,
    noCounterfactual: winners.filter((winner) => COUNTERFACTUAL_LABEL[winner] === undefined).length,
    protectedWithoutNpv: isProtected
      .filter((row) => parseDecimal(row.carryover_npv_amount) === null).length,
  };
}

/** A diverging series: one bar per row, the row's own key on the category axis. */
export interface DivergingSeries {
  readonly categories: readonly string[];
  readonly variance: readonly Datum[];
}

/**
 * DERIVE — the rate gap, `r̂ − r*`, in pairs a week, one bar per proposal.
 *
 * Both rates are units a week (bata-platform `planning/exit/terminal.py`
 * `required_depletion_rate` = (remaining − target) ÷ weeks; `cadence.py` `forecast_rate`), so
 * their difference is units a week too — not a share, and not ×100 (audit 2026-10-02 #5).
 * A subtraction of two published rates; neither rate is recomputed
 * here and neither is a ratio this module owns. A row whose rates will not parse is a
 * GAP in the series and never a nought bar: a proposal nobody could price is not a
 * proposal running exactly to plan.
 *
 * The category is `proposal_id` rather than the option, because the option repeats
 * across clusters and a category axis with a repeated member draws two bars in one slot.
 */
export function cadenceRateGap(rows: readonly CadenceProposalSummary[]): DivergingSeries {
  const variance = rows.map((row): Datum => {
    const forecast = parseDecimal(row.forecast_rate);
    const required = parseDecimal(row.required_rate);
    if (forecast === null || required === null) return { missing: 'not-reported' };
    return forecast - required;
  });
  return { categories: rows.map((row) => row.proposal_id), variance };
}

// ── X1 — the exit plan book ──────────────────────────────────────────────────

/**
 * Where a plan stands against its own target: ONE rule, in the plan book's own order, for every
 * count of the same thing on every screen (owner ruling, step 0: PROTECTED PREVAILS).
 *
 * 1. `protected`: flagged protected from markdown. A protected option above its target is
 *    protected, never off plan: carry-over options are governed by weeks of cover rather than by a
 *    ratio, and no markdown or exit action may be proposed on them. This holds whatever the ratio
 *    says, null included.
 * 2. `ratioUnstated`: not protected, and the ratio or the target is not published.
 * 3. `offPlan`: the ratio is above the target.
 * 4. `onPlan`: at or under it.
 *
 * Nothing is computed: the only arithmetic is the comparison of two published ratios (ADR 0060).
 */
export type PlanState = 'protected' | 'ratioUnstated' | 'offPlan' | 'onPlan';

type PlanStateInput = Pick<ExitPlanSummary, 'protected_from_markdown' | 'terminal_stock_ratio' | 'target_ratio'>;

export function planState(row: PlanStateInput): PlanState {
  if (row.protected_from_markdown) return 'protected';
  const ratio = parseDecimal(row.terminal_stock_ratio);
  const target = parseDecimal(row.target_ratio);
  if (ratio === null || target === null) return 'ratioUnstated';
  return ratio > target ? 'offPlan' : 'onPlan';
}

/** The catalogue key of each plan state, spelled out for the catalogue check. */
export const PLAN_STATE_KEY: Readonly<Record<PlanState, string>> = {
  protected: 'exit.state.protected',
  ratioUnstated: 'exit.state.ratioUnstated',
  offPlan: 'exit.state.offPlan',
  onPlan: 'exit.state.onPlan',
};

/** Whether a PROTECTED plan's published ratio is above its own target: the part of «protected» that would otherwise read as off plan. */
export function protectedAboveTarget(row: PlanStateInput): boolean {
  if (!row.protected_from_markdown) return false;
  const ratio = parseDecimal(row.terminal_stock_ratio);
  const target = parseDecimal(row.target_ratio);
  return ratio !== null && target !== null && ratio > target;
}

export interface PlanStanding {
  readonly plans: number;
  /**
   * Residual units summed over the rows that publish one, or `null` when none does.
   *
   * A sum over an empty set is nought in arithmetic and a lie on a screen: an estate
   * whose residual nobody has measured is not an estate holding nothing. `residualUnstated`
   * says how much of the book the sum leaves out.
   */
  readonly residualUnits: number | null;
  readonly residualUnstated: number;
  readonly targetResidualUnits: number;
  /** Plans OFF PLAN: the ratio is above the target and the plan is not protected (`planState`). */
  readonly aboveTarget: number;
  /** Plans on plan: not protected, ratio at or under the target. */
  readonly atOrUnderTarget: number;
  /** Plans not protected whose ratio or target is not published. Neither off plan nor on it. */
  readonly ratioUnstated: number;
  readonly awaitingSignOff: number;
  /** Protected plans, whatever their ratio says: the part of the book not judged against a ratio. */
  readonly protectedLines: number;
  /** Of the protected plans, those whose published ratio is above their target: protected, not off plan. */
  readonly protectedAboveTarget: number;
}

/**
 * DERIVE — the plan book's position. No ratio is computed and none is aggregated.
 *
 * ADR 0060 holds: the terminal-stock ratio is read from the row. The only arithmetic here
 * is a comparison of two published ratios and a sum of published unit counts.
 */
export function planStanding(rows: readonly ExitPlanSummary[]): PlanStanding {
  const residuals = rows
    .map((row) => row.residual_units)
    .filter((units): units is number => units !== null && units !== undefined);
  const states = rows.map(planState);
  return {
    plans: rows.length,
    residualUnits: residuals.length === 0
      ? null
      : residuals.reduce((sum, units) => sum + units, 0),
    residualUnstated: rows.length - residuals.length,
    targetResidualUnits: rows.reduce((sum, row) => sum + row.target_residual_units, 0),
    aboveTarget: states.filter((state) => state === 'offPlan').length,
    atOrUnderTarget: states.filter((state) => state === 'onPlan').length,
    ratioUnstated: states.filter((state) => state === 'ratioUnstated').length,
    awaitingSignOff: rows.filter((row) => !row.merchant_signed_off || !row.finance_signed_off).length,
    protectedLines: states.filter((state) => state === 'protected').length,
    protectedAboveTarget: rows.filter(protectedAboveTarget).length,
  };
}

/**
 * One lifecycle class's standing, for the estate landing surface.
 *
 * `worstRatio` is the HIGHEST published terminal-stock ratio in the class and
 * `worstTarget` is **that same row's** target — a pair a planner can chase to one option,
 * not an average. There is deliberately no class-level ratio here: a mean of ratios has a
 * denominator nobody chose, and ADR 0060 puts the one legitimate denominator in
 * `pub_exit_terminal_stock`. Two implementations of one ratio is the drift reqs/07 §3.1
 * test *(b)* exists to find.
 */
export interface LifecycleStanding {
  readonly lifecycleClass: string;
  readonly options: number;
  readonly worstRatio: number | null;
  readonly worstTarget: number | null;
  readonly worstOptionId: string | null;
  /** The worst option's plan — the record the class row drills to (`/exit/plans/:exitPlanId`). */
  readonly worstExitPlanId: string | null;
  /** Plans of the class OFF PLAN (above target, not protected): the plan book's own state. */
  readonly aboveTarget: number;
  /** Plans of the class, not protected, whose ratio or target is not published. */
  readonly ratioUnstated: number;
  /** Protected plans of the class, and those of them above their target (never counted off plan). */
  readonly protectedPlans: number;
  readonly protectedAboveTarget: number;
}

/** DERIVE — one standing per lifecycle class present, in code order. */
export function lifecycleStandings(
  rows: readonly ExitPlanSummary[],
): readonly LifecycleStanding[] {
  const classes = [...new Set(rows.map((row) => row.lifecycle_class))].sort();
  return classes.map((lifecycleClass) => {
    const members = rows.filter((row) => row.lifecycle_class === lifecycleClass);
    let worst: { ratio: number; target: number | null; optionId: string; exitPlanId: string } | null = null;
    for (const row of members) {
      const ratio = parseDecimal(row.terminal_stock_ratio);
      const target = parseDecimal(row.target_ratio);
      if (ratio === null) continue;
      if (worst === null || ratio > worst.ratio) {
        worst = { ratio, target, optionId: row.option_id, exitPlanId: row.exit_plan_id };
      }
    }
    return {
      lifecycleClass,
      options: members.length,
      worstRatio: worst === null ? null : worst.ratio,
      worstTarget: worst === null ? null : worst.target,
      worstOptionId: worst === null ? null : worst.optionId,
      worstExitPlanId: worst === null ? null : worst.exitPlanId,
      aboveTarget: members.filter((row) => planState(row) === 'offPlan').length,
      ratioUnstated: members.filter((row) => planState(row) === 'ratioUnstated').length,
      protectedPlans: members.filter((row) => planState(row) === 'protected').length,
      protectedAboveTarget: members.filter(protectedAboveTarget).length,
    };
  });
}

/**
 * DERIVE — the plans of one lifecycle class that stand in one state: a segment of the Position
 * chart. The same `planState` as the plan book's state column and the cards, so a class's segments
 * add up to its plans and each equals the book's rows of that class and state.
 */
export function plansInClassState(rows: readonly ExitPlanSummary[], lifecycleClass: string, state: PlanState): number {
  return rows.filter((row) => row.lifecycle_class === lifecycleClass && planState(row) === state).length;
}

// ── X6 — floor-space handover ────────────────────────────────────────────────

/**
 * reqs/05 §8 criterion 8 — the minimum alert lead, in weeks.
 *
 * The acceptance criterion's own figure, not a tunable. G1 makes a threshold in source a
 * defect where the threshold is POLICY; this one is the published criterion the build is
 * measured against, and it is named once so that the day it becomes a registry parameter
 * there is one line to change and one comment to delete.
 */
export const MINIMUM_ALERT_LEAD_WEEKS = 4;

export interface HandoverStanding {
  readonly clusters: number;
  readonly ready: number;
  readonly atRisk: number;
  readonly notReady: number;
  /** Rows carrying a state this build has no label for. Counted, never reclassified. */
  readonly stateUnrecognised: number;
  readonly shortLead: number;
  /** Projected units above the cluster threshold, summed over the rows that breach. */
  readonly excessUnits: number;
}

/** DERIVE — the go/no-go position across a page of readiness rows. */
export function handoverStanding(
  rows: readonly HandoverReadinessSummary[],
): HandoverStanding {
  const state = (row: HandoverReadinessSummary) => row.state.trim().toLowerCase();
  return {
    clusters: rows.length,
    ready: rows.filter((row) => state(row) === 'ready').length,
    atRisk: rows.filter((row) => state(row) === 'at_risk').length,
    notReady: rows.filter((row) => state(row) === 'not_ready').length,
    stateUnrecognised: rows.filter((row) => HANDOVER_STATE_LABEL[state(row)] === undefined).length,
    shortLead: rows.filter((row) => row.alert_lead_weeks < MINIMUM_ALERT_LEAD_WEEKS).length,
    excessUnits: rows.reduce(
      (sum, row) => sum + Math.max(0, row.projected_units_at_floor_set - row.threshold_units),
      0,
    ),
  };
}

// ── X4 and X5 — exit-channel routing ─────────────────────────────────────────

/**
 * Whether a route is a candidate a planner could actually send stock down.
 *
 * Three facts and not one, because the service publishes three: a route the brand rules
 * excluded was never in the race, a permitted route with no realisation history was in
 * the race and could not be priced, and a permitted, scored route with a number lost or
 * won on it. Collapsing any two loses the reason a planner is owed (reqs/05 §5.1).
 */
export function isScorable(route: RouteRealisation): boolean {
  return route.permitted !== false
    && route.was_scored !== false
    && parseDecimal(route.net_realisation) !== null;
}

/**
 * The routing position over `GET /exit/routing`: how many lots, how many units.
 *
 * Nothing about ROUTES is counted here, deliberately. The platform publishes ONE entry in
 * `considered` per decision — the winning route — and an empty `gated_out` (bata-platform
 * `routing_row`: "gated_out stays empty because nothing here knows which routes the brand rules
 * excluded"). The losers and the excluded routes exist only on `/exit/routing/{id}/routes`. So a
 * count of routes scored, not scored or gated out over this list would be 0 where the truth is
 * "not published" — a false zero — and the screen states those figures as absent with that reason.
 * The valuations panel counts them from `/routes`, where the routes are.
 */
export interface RouteStanding {
  readonly lots: number;
  readonly units: number;
}

/** DERIVE — the routing position over a page of decisions. */
export function routeStanding(rows: readonly RoutingSummary[]): RouteStanding {
  return {
    lots: rows.length,
    units: rows.reduce((sum, row) => sum + row.units, 0),
  };
}

// ── X7 — returns ─────────────────────────────────────────────────────────────

export interface ReturnsStanding {
  readonly dispositions: number;
  /**
   * Every returned unit, and the segregated share of it. Real figures again since the screen
   * reads `GET /exit/returns?segregation=all` (bata-platform DEC-111 follow-ups): both sides,
   * told apart by `counts_toward_full_price`. Before it the read carried only the full-price side
   * and both figures were absent (audit 2026-10-02 #22). Over the default full-price read these
   * totals would mean nothing, so callers read with `segregation=all` (`RETURNS_SEGREGATION`).
   */
  readonly units: number;
  readonly fullPriceUnits: number;
  readonly segregatedUnits: number;
  readonly unpricedDispositions: number;
}

/** The returns read's side: both, so the totals split on `counts_toward_full_price` are real. */
export const RETURNS_SEGREGATION = 'all' as const;

/** DERIVE — the disposition log's totals. Sums of published unit counts only. */
export function returnsStanding(
  rows: readonly ReturnDispositionSummary[],
): ReturnsStanding {
  const units = (side: readonly ReturnDispositionSummary[]): number => side.reduce((sum, row) => sum + row.units, 0);
  return {
    dispositions: rows.length,
    units: units(rows),
    fullPriceUnits: units(rows.filter((row) => row.counts_toward_full_price)),
    segregatedUnits: units(rows.filter((row) => !row.counts_toward_full_price)),
    unpricedDispositions: rows
      .filter((row) => parseDecimal(row.realisation_per_unit) === null).length,
  };
}

// ── the join nothing else makes: the stranded lot ────────────────────────────

/**
 * The handover horizon a stranded lot is measured inside, in weeks.
 *
 * The same figure as {@link MINIMUM_ALERT_LEAD_WEEKS} and named separately on purpose:
 * one is the lead an alert must give, the other is the window an option is inside. They
 * coincide today because reqs/05 §8 criterion 8 fixes both at four, and the day one moves
 * the other should not move with it by accident.
 */
export const HANDOVER_HORIZON_WEEKS = 4;

export type StrandedVerdict = 'stranded' | 'not-stranded' | 'undetermined';

/**
 * One option, and the three facts reqs/05 produces separately and never joins.
 *
 * * **At its price floor** — `state = floor_reached` on the cadence queue (§3.2 rule 7:
 *   the cadence refuses to walk below the floor, so the option leaves the price stream);
 * * **no permitted route scorable** — every route on its routing decision either gated
 *   out before scoring or carrying no realisation (§3.5, §8 criterion 10);
 * * **inside the handover horizon** — `weeks_to_handover` at or under four (§8 criterion 8).
 *
 * All three together mean the option cannot be repriced, cannot be routed and is running
 * out of calendar. Each state is published on its own screen and the join is published
 * nowhere, which is why it is computed here and asserted in `api.test.ts`.
 *
 * **A missing input is `undetermined`, never `not-stranded` — unless another input is KNOWN to
 * be false.** Stranded is the conjunction of the three facts, so the logic is three-valued: any
 * fact that is `false` settles it (`not-stranded`, whatever the other two say, because a false
 * conjunct makes the conjunction false); otherwise any `null` leaves it `undetermined`; otherwise
 * all three are `true`. An option with no routing decision has not been shown to have a route, and
 * one with no `weeks_to_handover` has not been shown to have time — reading either absence as
 * safety is the failure this chapter is written against — but an option that is plainly above its
 * floor is not stranded however little else is known about it, and calling it «undetermined»
 * would put a settled row among the ones nobody could judge.
 */
export interface StrandedLot {
  /** The plan the lot is joined on — one lot per plan, and the key its row drills to. */
  readonly exitPlanId: string;
  readonly optionId: string;
  readonly countryKey: string;
  readonly verdict: StrandedVerdict;
  /** `null` where the cadence queue carries no proposal for the option. */
  readonly atFloor: boolean | null;
  /** `null` where no routing decision exists for the option. */
  readonly noScorableRoute: boolean | null;
  readonly weeksToHandover: number | null;
  readonly priceFloorAmount: number | null;
  readonly currencyCode: string;
  /**
   * The other plans in scope this lot's evidence cannot be told apart from. A cadence proposal and a
   * routing decision carry the plan they were made for (`exitPlanId`, platform ADR 6194), and a row
   * that does is joined to that plan alone: it is nobody else's evidence. A row that does not — an
   * older platform, a routing evaluation built from a request and so with no stored plan — falls back
   * to the option and the market (the queue also the cluster), while a plan is one option × market ×
   * season × cluster (`exit_plan_record`, bata-platform `repositories/exit.py`): two plans of one
   * option in one market then read the SAME fallback rows, and neither can be shown to own them. Empty
   * where every row the lot reads is joined by plan id, or where the lot's evidence is its own. The
   * fallback join is kept — it is not guessed at — and the screen says so on these rows.
   */
  readonly sharesEvidenceWith: readonly string[];
}

/** The three-valued conjunction of the stranded facts: any `false` settles it, then any `null`, then `true`. */
export function strandedVerdict(
  atFloor: boolean | null, noScorableRoute: boolean | null, withinHorizon: boolean | null,
): StrandedVerdict {
  const facts = [atFloor, noScorableRoute, withinHorizon];
  if (facts.some((fact) => fact === false)) return 'not-stranded';
  if (facts.some((fact) => fact === null)) return 'undetermined';
  return 'stranded';
}

/** The plan a stored evidence row names (`exitPlanId`), or `null` where it names none (see {@link StrandedLot.sharesEvidenceWith}). */
function planOf(row: CadenceProposalSummary | RoutingSummary): string | null {
  const id = row.exit_plan_id ?? null;
  return id === null || id.trim() === '' ? null : id;
}

/**
 * Whether a cadence proposal belongs to a plan: the plan it names, exactly. A proposal that names none
 * falls back to the same option in the same market, and — where the plan is one cluster's — the same
 * cluster; a plan with no cluster is the option's across clusters. The fallback carries no season.
 */
function proposalBelongsTo(plan: ExitPlanSummary, row: CadenceProposalSummary): boolean {
  const named = planOf(row);
  if (named !== null) return named === plan.exit_plan_id;
  return row.option_id === plan.option_id && row.country_key === plan.country_key
    && (plan.cluster_id === null || plan.cluster_id === undefined || row.cluster_id === plan.cluster_id);
}

/** Whether a routing decision belongs to a plan: the plan it names, exactly; else the option in the market is all it carries. */
function routingBelongsTo(plan: ExitPlanSummary, row: RoutingSummary): boolean {
  const named = planOf(row);
  if (named !== null) return named === plan.exit_plan_id;
  return row.option_id === plan.option_id && row.country_key === plan.country_key;
}

/**
 * DERIVE — the join, one row per exit plan.
 *
 * The plan book is the left side because it is the register of what is being exited:
 * a proposal or a routing decision with no plan behind it is a defect upstream, not a
 * row to invent a plan for. The join key is the plan id the evidence row names; only a row
 * that names none is joined on the option and the market (see {@link proposalBelongsTo}).
 */
export function strandedLots(
  plans: readonly ExitPlanSummary[],
  proposals: readonly CadenceProposalSummary[],
  routings: readonly RoutingSummary[],
): readonly StrandedLot[] {
  const plansOfOption = new Map<string, ExitPlanSummary[]>();
  for (const plan of plans) plansOfOption.set(plan.option_id, [...(plansOfOption.get(plan.option_id) ?? []), plan]);
  return plans.map((plan): StrandedLot => {
    const forOption = proposals.filter((row) => proposalBelongsTo(plan, row));
    const routed = routings.filter((row) => routingBelongsTo(plan, row));
    // Another plan of the option that the same evidence row also belongs to: the evidence is shared. A row joined by the
    // plan it names belongs to that plan alone, so only the fallback rows can be shared.
    const neighbours = (plansOfOption.get(plan.option_id) ?? []).filter((other) => other.exit_plan_id !== plan.exit_plan_id);
    const shared = new Set<string>();
    for (const row of forOption) if (planOf(row) === null) for (const other of neighbours) if (proposalBelongsTo(other, row)) shared.add(other.exit_plan_id);
    for (const row of routed) if (planOf(row) === null) for (const other of neighbours) if (routingBelongsTo(other, row)) shared.add(other.exit_plan_id);
    const atFloor = forOption.length === 0
      ? null
      : forOption.some((row) => (row.state ?? '').trim().toLowerCase() === 'floor_reached');
    // `false` is a true fact here although `considered` holds only the winning route: a stored
    // routing decision implies a scored winning route, so "a route can be scored" holds, and only
    // a winner with no net realisation turns it to `true`. The routing headline's route COUNTS are
    // a different matter: this list cannot give them (see `RouteStanding`).
    const noScorableRoute = routed.length === 0
      ? null
      : routed.every((row) => (row.considered ?? []).every((route) => !isScorable(route)));
    const weeks = plan.weeks_to_handover ?? null;
    const withinHorizon = weeks === null ? null : weeks <= HANDOVER_HORIZON_WEEKS;
    const verdict = strandedVerdict(atFloor, noScorableRoute, withinHorizon);
    return {
      exitPlanId: plan.exit_plan_id,
      optionId: plan.option_id,
      countryKey: plan.country_key,
      verdict,
      atFloor,
      noScorableRoute,
      weeksToHandover: weeks,
      priceFloorAmount: parseDecimal(plan.price_floor_amount),
      currencyCode: plan.currency_code,
      sharesEvidenceWith: [...shared],
    };
  });
}

export interface StrandedStanding {
  readonly options: number;
  readonly stranded: number;
  readonly notStranded: number;
  readonly undetermined: number;
}

/** DERIVE — the three counts, and the third is never folded into the second. */
export function strandedStanding(lots: readonly StrandedLot[]): StrandedStanding {
  return {
    options: lots.length,
    stranded: lots.filter((lot) => lot.verdict === 'stranded').length,
    notStranded: lots.filter((lot) => lot.verdict === 'not-stranded').length,
    undetermined: lots.filter((lot) => lot.verdict === 'undetermined').length,
  };
}

/**
 * DERIVE — stranded first, then the rows nobody could judge, then the settled ones.
 *
 * A row that IS a decision belongs above a row that is a gap in the evidence, and both belong
 * above the settled ones. Stable, so the join's own order holds within each verdict. One
 * function for the table and its CSV, because the table draws only the head of this order
 * (`docs/adr/9008`) and the file must continue it rather than start another.
 */
export function orderStranded(lots: readonly StrandedLot[]): readonly StrandedLot[] {
  const rank = (lot: StrandedLot): number => (
    lot.verdict === 'stranded' ? 0 : lot.verdict === 'undetermined' ? 1 : 2
  );
  return [...lots].sort((a, b) => rank(a) - rank(b));
}

// ── the measured terminal position — contract 0.23 ───────────────────────────

/**
 * How many distinct intake conventions one page of terminal positions was measured under.
 *
 * reqs/05 §2 names two live conventions whose answers differ by roughly five points on
 * the same country. `intake_definition_id` travels with every ratio for that reason, and
 * a screen that summed or compared rows across two of them would be reporting a
 * difference in bookkeeping as a difference in trading. Counting them is not a judgement
 * about which is right — it is the precondition for knowing whether a comparison is one.
 */
export function intakeDefinitions(
  rows: readonly TerminalPositionSummary[],
): readonly string[] {
  return [...new Set(rows.map((row) => row.intake_definition_id))].sort();
}

export interface TerminalStandingRow {
  readonly key: string;
  readonly row: TerminalPositionSummary;
  readonly ratio: number | null;
  readonly target: number | null;
  readonly sellThrough: number | null;
}

export interface TerminalStanding {
  readonly positions: number;
  /** Rows the platform itself marked as breaching. READ, never recomputed — ADR 0060. */
  readonly breaching: number;
  readonly withinTarget: number;
  /** Measured saleable units, summed. A count of published integers, not a measure. */
  readonly saleableUnits: number;
  readonly projectedResidualUnits: number;
  readonly targetResidualUnits: number;
  readonly intakeUnits: number;
  readonly definitions: readonly string[];
  readonly rows: readonly TerminalStandingRow[];
}

/**
 * DERIVE — counts and sums over one page of measured terminal positions.
 *
 * **There is no ratio anywhere in this function and that is the rule, not an omission.**
 * `terminal_stock_ratio` arrives already computed from `exit_terminal_position`, which is
 * the single site of that arithmetic because chapter 04's markdown optimisation solves
 * against the same number — two implementations of one ratio is the reconciliation drift
 * reqs/07 §3.1 test *(b)* exists to find (ADR 0060). `breaches_target` is likewise the
 * stored verdict, read rather than re-derived, so this read, the alert seed and the screen
 * cannot disagree (G11).
 *
 * The per-row decimals are parsed for display only — `parseDecimal` returns `null` rather
 * than `0` on anything it cannot read, so a ratio the platform declined to publish renders
 * as an absence and never as an estate that has cleared.
 */
export function terminalStanding(
  rows: readonly TerminalPositionSummary[],
): TerminalStanding {
  const breaching = rows.filter((row) => row.breaches_target).length;
  return {
    positions: rows.length,
    breaching,
    withinTarget: rows.length - breaching,
    saleableUnits: rows.reduce((total, row) => total + row.saleable_units, 0),
    projectedResidualUnits: rows.reduce(
      (total, row) => total + row.projected_residual_units, 0,
    ),
    targetResidualUnits: rows.reduce((total, row) => total + row.target_residual_units, 0),
    intakeUnits: rows.reduce((total, row) => total + row.intake_units, 0),
    definitions: intakeDefinitions(rows),
    rows: rows.map((row) => ({
      // Plan, cluster and week together: one plan is measured at several clusters and
      // several weeks, and keying on the plan alone would collapse rows React must keep
      // apart. `cluster_id` is `''` for the all-clusters row, which is a real value.
      key: `${row.exit_plan_id}|${row.cluster_id}|${row.as_of_week}`,
      row,
      ratio: parseDecimal(row.terminal_stock_ratio),
      target: parseDecimal(row.target_ratio),
      sellThrough: parseDecimal(row.terminal_sell_through),
    })),
  };
}

// ── the signature gap — reqs/05 §8.3 ─────────────────────────────────────────

export interface UnsignedStanding {
  readonly plans: number;
  /** Waiting on a merchant, whether or not Finance has signed. */
  readonly awaitingMerchant: number;
  /** Waiting on Finance, whether or not the merchant has signed. */
  readonly awaitingFinance: number;
  /** Waiting on both. Counted apart because it is one row and two chases. */
  readonly awaitingBoth: number;
}

/**
 * DERIVE — who each unsigned plan is waiting on.
 *
 * **Two booleans, never one flag.** Which signature is outstanding decides who the row
 * goes to, and a single "incomplete" count would send a merchant to chase Finance. The
 * three counts deliberately overlap — a plan waiting on both is in all three — because
 * the two chase lists are what a planner works from, and making them disjoint would
 * under-report each one by the size of the intersection.
 */
export function unsignedStanding(
  rows: readonly UnsignedPlanSummary[],
): UnsignedStanding {
  return {
    plans: rows.length,
    awaitingMerchant: rows.filter((row) => !row.merchant_signed_off).length,
    awaitingFinance: rows.filter((row) => !row.finance_signed_off).length,
    awaitingBoth: rows.filter(
      (row) => !row.merchant_signed_off && !row.finance_signed_off,
    ).length,
  };
}
